import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database, Json } from "@/integrations/supabase/types";
import type { MedicalPrescriptionInput } from "@/lib/validations/medical-prescription";
import type { ExamPrescriptionInput } from "@/lib/validations/exam-prescription";
import i18n from "@/i18n";
import type { PosologyMode } from "@/lib/prescriptions/posology";
import { normalizeAdministrationRouteValue } from "@/lib/prescriptions/prescription-fields";

type MedicalPrescriptionRow = Database["public"]["Tables"]["medical_prescriptions"]["Row"];
type LegacyMedicalPrescriptionItemRow = Database["public"]["Tables"]["medical_prescription_items"]["Row"];
type MedicalPrescriptionExamItemRow =
  Database["public"]["Tables"]["medical_prescription_exam_items"]["Row"];
type MedicalPrescriptionInsert = Database["public"]["Tables"]["medical_prescriptions"]["Insert"];
type MedicalPrescriptionUpdate = Database["public"]["Tables"]["medical_prescriptions"]["Update"];
type MedicalPrescriptionItemInsert = Database["public"]["Tables"]["medical_prescription_items"]["Insert"];
type MedicalPrescriptionExamItemInsert = Database["public"]["Tables"]["medical_prescription_exam_items"]["Insert"];

export type MedicalPrescriptionItemRow = Omit<
  LegacyMedicalPrescriptionItemRow,
  "dosage" | "frequency" | "duration" | "instructions"
> & {
  posology_mode: PosologyMode | null;
  free_text_posology: string | null;
  dose_amount: string | null;
  dose_unit: string | null;
  frequency_value: string | null;
  frequency_unit: string | null;
  duration_value: string | null;
  duration_unit: string | null;
};

export type PrescriptionWithItems = MedicalPrescriptionRow & {
  issuer_address?: string | null;
  items: MedicalPrescriptionItemRow[];
  exam_items: MedicalPrescriptionExamItemRow[];
  vet?: {
    id: string;
    full_name: string | null;
    phone?: string | null;
    mapa_number?: string | null;
    address?: string | null;
  } | null;
  clinic?: { id: string; name: string | null } | null;
  pets?: {
    id: string;
    name: string;
    birth_date?: string | null;
    species?: { name: string | null } | null;
    breed?: { name: string | null } | null;
    gender?: { name: string | null } | null;
    owners?: {
      id: string;
      name: string;
      document?: string | null;
      addresses?: Array<{
        street?: string | null;
        number?: string | null;
        neighborhood?: string | null;
        is_primary?: boolean | null;
        deleted_at?: string | null;
        city?: { name?: string | null; state?: { code?: string | null } | null } | null;
      }> | null;
    } | null;
    weights?: {
      weight?: number | null;
      measured_at?: string | null;
      medical_record_id?: string | null;
      deleted_at?: string | null;
    }[] | null;
  } | null;
};

const prescriptionSelect = `
  *,
  items:medical_prescription_items(*),
  exam_items:medical_prescription_exam_items(*),
  vet:profiles!medical_prescriptions_vet_user_id_fkey(id, full_name, phone, mapa_number, address),
  clinic:clinics(id, name),
  pets:pets(
    id,
    name,
    birth_date,
    species:species(name),
    breed:breeds(name),
    gender:pet_genders(name),
    owners:owners(
      id,
      name,
      document,
      addresses:owners_addresses(street, number, neighborhood, is_primary, deleted_at, city:cities(name, state:states(code)))
    ),
    weights:pet_weights(weight, measured_at, medical_record_id, deleted_at)
  )
`;

const fetchPrescriptionById = async (prescriptionId: string) => {
  const { data, error } = await supabase
    .from("medical_prescriptions")
    .select(prescriptionSelect)
    .eq("id", prescriptionId)
    .single();

  if (error) throw error;
  return data as PrescriptionWithItems;
};

const invalidatePrescriptionHistoryQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  petId?: string | null,
) => {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: ["patient-history"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["patient-history-feed"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["owner-history-timeline"], refetchType: "all" }),
    queryClient.invalidateQueries({ queryKey: ["owner-history-feed"], refetchType: "all" }),
  ];

  if (petId) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: ["patient-history", petId], refetchType: "all" }),
    );
  }

  return Promise.all(invalidations).then(() => undefined);
};

const upsertPrescriptionInCache = (
  current: PrescriptionWithItems[] | undefined,
  next: PrescriptionWithItems,
) => {
  if (!current) return [next];
  const filtered = current.filter((item) => item.id !== next.id);
  return [next, ...filtered].sort((a, b) => {
    const left = new Date(a.issued_at ?? a.created_at ?? 0).getTime();
    const right = new Date(b.issued_at ?? b.created_at ?? 0).getTime();
    return right - left;
  });
};

export const useMedicalPrescriptions = (petId?: string, appointmentId?: string, medicalRecordId?: string) => {
  return useQuery({
    queryKey: ["medical-prescriptions", petId, appointmentId, medicalRecordId],
    enabled: Boolean(petId) || Boolean(appointmentId) || Boolean(medicalRecordId),
    queryFn: async () => {
      let query = supabase
        .from("medical_prescriptions")
        .select(prescriptionSelect)
        .is("deleted_at", null)
        .order("issued_at", { ascending: false });

      if (petId) {
        query = query.eq("pet_id", petId);
      }

      if (appointmentId) {
        query = query.eq("appointment_id", appointmentId);
      }

      if (medicalRecordId) {
        query = query.eq("medical_record_id", medicalRecordId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as PrescriptionWithItems[];
    },
  });
};

type CreatePrescriptionPayload = (MedicalPrescriptionInput | ExamPrescriptionInput) & {
  vet_user_id?: string;
  crmv?: string | null;
  mapa_number?: string | null;
  control_program?: string | null;
  control_identifier?: string | null;
  issuer_address?: string | null;
};

type UpdatePrescriptionPayload = CreatePrescriptionPayload & {
  id: string;
  expected_updated_at: string | null;
};

const normalizeId = (id?: string | null): string | null => {
  if (!id || id.trim() === "") return null;
  return id;
};


const buildPrescriptionBasePayload = (
  payload: CreatePrescriptionPayload | UpdatePrescriptionPayload,
  vetId: string,
): MedicalPrescriptionInsert | MedicalPrescriptionUpdate => ({
  pet_id: payload.pet_id,
  clinic_id: payload.clinic_id,
  appointment_id: normalizeId(payload.appointment_id),
  medical_record_id: normalizeId(payload.medical_record_id),
  vet_user_id: vetId,
  type: payload.type,
  control_program: payload.control_program ? payload.control_program : null,
  control_identifier: payload.control_identifier ? payload.control_identifier : null,
  control_regime: (payload as { control_regime?: string }).control_regime ?? "none",
  validity_in_days: payload.validity_in_days ?? null,
  observations: payload.observations ? payload.observations : null,
  crmv: payload.crmv ? payload.crmv : null,
  mapa_number: payload.mapa_number ? payload.mapa_number : null,
  manipulated_payload: payload.type === "manipulated"
    ? (payload.manipulated_payload as unknown as Json)
    : null,
  ...({
    buyer_mode: (payload as { buyer_mode?: string }).buyer_mode ?? "blank",
    buyer_snapshot:
      (payload as { buyer_mode?: string }).buyer_mode === "filled"
        ? ((payload as { buyer_snapshot?: unknown }).buyer_snapshot ?? null)
        : null,
  } as Record<string, unknown>),
  ...("issuer_address" in payload
    ? ({ issuer_address: payload.issuer_address ? payload.issuer_address.trim() : null } as never)
    : {}),
});

const buildStandardPrescriptionItems = (
  prescriptionId: string,
  payload: CreatePrescriptionPayload | UpdatePrescriptionPayload,
): MedicalPrescriptionItemInsert[] => {
  if (payload.type === "manipulated") {
    const components = payload.manipulated_payload.components ?? [];
    return components
      .filter((component) => component?.medication_id && component.medication_id !== "")
      .map((component) => ({
        prescription_id: prescriptionId,
        medication_id: component.medication_id ?? null,
        medication_name: component.name,
        medication_concentration: component.concentration || null,
        pharmaceutical_form: null,
        administration_route: null,
        presentation: null,
        posology_mode: "automatic",
        free_text_posology: null,
        dose_amount: null,
        dose_unit: null,
        frequency_value: null,
        frequency_unit: null,
        duration_value: null,
        duration_unit: null,
      }));
  }

  if (payload.type === "exam") {
    return [];
  }

  return payload.items.map((item) => ({
    prescription_id: prescriptionId,
    medication_id: item.medication_id && item.medication_id !== "" ? item.medication_id : null,
    medication_name: item.medication_name,
    medication_concentration: item.medication_concentration || null,
    pharmaceutical_form: item.pharmaceutical_form || null,
    administration_route: normalizeAdministrationRouteValue(item.administration_route),
    presentation: item.presentation || null,
    posology_mode: item.posology_mode,
    free_text_posology: item.free_text_posology || null,
    dose_amount: item.dose_amount || null,
    dose_unit: item.dose_unit || null,
    frequency_value: item.frequency_value || null,
    frequency_unit: item.frequency_unit || null,
    duration_value: item.duration_value || null,
    duration_unit: item.duration_unit || null,
  }));
};

const buildExamPrescriptionItems = (
  prescriptionId: string,
  payload: CreatePrescriptionPayload | UpdatePrescriptionPayload,
): MedicalPrescriptionExamItemInsert[] => {
  if (payload.type !== "exam") {
    return [];
  }

  return payload.items.map((item) => ({
    prescription_id: prescriptionId,
    exam_type_id: item.exam_type_id && item.exam_type_id !== "" ? item.exam_type_id : null,
    exam_name: item.exam_name,
    instructions: item.instructions || null,
  }));
};

type SaveAttempt = { documentId: string; requestId: string };
const savePrescriptionAtomically = async (
  payload: CreatePrescriptionPayload | UpdatePrescriptionPayload,
  vetId: string,
  attempt: SaveAttempt,
  create: boolean,
) => {
  const args = {
    p_id: attempt.documentId,
    p_request_id: attempt.requestId,
    p_document: buildPrescriptionBasePayload(payload, vetId),
    p_items: buildStandardPrescriptionItems(attempt.documentId, payload),
    p_exam_items: buildExamPrescriptionItems(attempt.documentId, payload),
    p_create: create,
    p_expected_updated_at: "expected_updated_at" in payload ? payload.expected_updated_at : null,
  };
  // New RPC is typed locally until generated schema types are refreshed externally.
  const rpc = supabase as unknown as {
    rpc(name: "save_medical_prescription_atomic", values: typeof args): Promise<{ data: string | null; error: { message: string } | null }>;
  };
  const { data, error } = await rpc.rpc("save_medical_prescription_atomic", args);
  if (error) throw error;
  if (data !== attempt.documentId) throw new Error("prescription_save_incomplete");
  return fetchPrescriptionById(data);
};
const saveErrorMessage = (error: unknown, fallback: string) => {
  const message = error && typeof error === "object" && "message" in error ? String(error.message) : "";
  if (message.includes("prescription_version_conflict")) return i18n.t("prescriptions:composer.versionConflict");
  if (message.includes("prescription_request_conflict")) return i18n.t("prescriptions:composer.requestConflict");
  if (message.includes("prescription_locked")) return i18n.t("prescriptions:composer.documentLocked");
  return i18n.t(fallback);
};

export const useCreateMedicalPrescription = () => {
  const queryClient = useQueryClient();
  const pendingAttempt = useRef<SaveAttempt | null>(null);

  return useMutation({
    mutationFn: async (payload: CreatePrescriptionPayload) => {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const vetId = payload.vet_user_id ?? userData.user?.id;
      if (!vetId) throw new Error(i18n.t("prescriptions:composer.notAuthenticated"));
      pendingAttempt.current ??= { documentId: crypto.randomUUID(), requestId: crypto.randomUUID() };
      const prescription = await savePrescriptionAtomically(payload, vetId, pendingAttempt.current, true);
      // Retain the attempt across RPC/response/read failures; retry is a replay.
      pendingAttempt.current = null;
      return prescription;
    },
    onSuccess: async (createdPrescription, variables) => {
      queryClient.setQueryData<PrescriptionWithItems[]>(
        ["medical-prescriptions", variables.pet_id, undefined, undefined],
        (current) => upsertPrescriptionInCache(current, createdPrescription),
      );
      if (variables.appointment_id) {
        queryClient.setQueryData<PrescriptionWithItems[]>(
          ["medical-prescriptions", undefined, variables.appointment_id, undefined],
          (current) => upsertPrescriptionInCache(current, createdPrescription),
        );
      }
      if (variables.medical_record_id) {
        queryClient.setQueryData<PrescriptionWithItems[]>(
          ["medical-prescriptions", undefined, undefined, variables.medical_record_id],
          (current) => upsertPrescriptionInCache(current, createdPrescription),
        );
      }
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: ["medical-prescriptions", variables.pet_id],
          refetchType: "all",
        }),
      ];

      if (variables.appointment_id) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: ["medical-prescriptions", undefined, variables.appointment_id],
            refetchType: "all",
          }),
        );
      }
      if (variables.medical_record_id) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: ["medical-prescriptions", undefined, undefined, variables.medical_record_id],
            refetchType: "all",
          }),
        );
      }
      invalidations.push(invalidatePrescriptionHistoryQueries(queryClient, variables.pet_id));
      await Promise.all(invalidations);

      console.info("[prescriptions] created", {
        prescriptionId: createdPrescription.id,
        petId: variables.pet_id,
        appointmentId: variables.appointment_id ?? null,
        medicalRecordId: variables.medical_record_id ?? null,
        clinicId: variables.clinic_id,
      });
      toast.success(i18n.t("prescriptions:composer.createdSuccess"));
    },
    onError: (error) => {
      console.error("Erro ao criar receita:", error);
      toast.error(saveErrorMessage(error, "prescriptions:composer.createdError"));
    },
  });
};

export const useUpdateMedicalPrescription = () => {
  const queryClient = useQueryClient();
  const pendingAttempt = useRef<(SaveAttempt & { fingerprint: string }) | null>(null);

  return useMutation({
    mutationFn: async (payload: UpdatePrescriptionPayload) => {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      const vetId = payload.vet_user_id ?? userData.user?.id;
      if (!vetId) throw new Error(i18n.t("prescriptions:composer.notAuthenticated"));

      const fingerprint = JSON.stringify(payload);
      if (pendingAttempt.current?.fingerprint !== fingerprint) {
        pendingAttempt.current = { documentId: payload.id, requestId: crypto.randomUUID(), fingerprint };
      }
      const prescription = await savePrescriptionAtomically(payload, vetId, pendingAttempt.current, false);
      pendingAttempt.current = null;
      return prescription;
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["medical-prescriptions"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["prescription-with-pet", variables.id], refetchType: "all" }),
        invalidatePrescriptionHistoryQueries(queryClient, variables.pet_id),
      ]);
      toast.success(i18n.t("prescriptions:composer.updatedSuccess"));
    },
    onError: (error) => {
      console.error("Erro ao atualizar receita:", error);
      toast.error(saveErrorMessage(error, "prescriptions:composer.updatedError"));
    },
  });
};

export const useDeleteMedicalPrescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (prescriptionId: string) => {
      const { error } = await supabase
        .from("medical_prescriptions")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", prescriptionId);

      if (error) throw error;
      return prescriptionId;
    },
    onSuccess: async (prescriptionId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["medical-prescriptions"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["prescription-with-pet", prescriptionId], refetchType: "all" }),
        invalidatePrescriptionHistoryQueries(queryClient),
      ]);
      toast.success(i18n.t("patients:timeline.prescriptionDeleted"));
    },
    onError: (error) => {
      console.error("Erro ao remover receita:", error);
      toast.error(i18n.t("patients:timeline.deletePrescriptionError"));
    },
  });
};
